import { useState, useEffect, useCallback, useRef } from 'react';
import { GitBranch, Plus, Trash2, Zap, ArrowRight, Clock, RefreshCw, Sparkles, Pencil } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import {
  workflowService,
  type WorkflowRule,
  type WorkflowCanvasNode,
  type WorkflowExecutionRow,
} from '@core/supabase/services/workflowService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { orgService } from '@core/supabase/services/orgService';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

const STORAGE_KEY = 'ggj_b2b_workflows';
const CANVAS_STORAGE_KEY = 'ggj_b2b_workflow_canvas';

const TRIGGERS = ['Application Created', 'Status Changed', 'Email CTA Response', 'Schedule'];
const ACTIONS = ['Update Status', 'Send Email', 'Send Notification'];

export function WorkflowsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;

  const [workflows, setWorkflows] = useState<WorkflowRule[]>(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', trigger: TRIGGERS[0], action: ACTIONS[0] });
  const [canvasNodes, setCanvasNodes] = useState<WorkflowCanvasNode[]>(() => {
    try {
      const s = localStorage.getItem(CANVAS_STORAGE_KEY);
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [draggedPaletteItem, setDraggedPaletteItem] = useState<{ type: 'trigger' | 'action'; label: string } | null>(null);
  const [draggingNode, setDraggingNode] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [persistError, setPersistError] = useState<string | null>(null);
  const [persisting, setPersisting] = useState(false);
  const [executions, setExecutions] = useState<WorkflowExecutionRow[]>([]);
  const [execLoading, setExecLoading] = useState(false);
  /** PRD §8.11 — Automations [ List ] [ Canvas ] */
  const [workspaceTab, setWorkspaceTab] = useState<'list' | 'canvas'>('list');
  /** When set, form updates this rule instead of creating a new one */
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  /** After remote/local hydrate, allow debounced saves (skip initial empty save). */
  const allowSaveRef = useRef(false);

  useEffect(() => {
    allowSaveRef.current = false;
    setHydrated(false);
    setPersistError(null);

    if (!orgId) {
      setHydrated(true);
      allowSaveRef.current = true;
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const remote = await workflowService.getWorkspace(orgId);

        let localW: WorkflowRule[] = [];
        let localC: WorkflowCanvasNode[] = [];
        try {
          const ws = localStorage.getItem(STORAGE_KEY);
          const cs = localStorage.getItem(CANVAS_STORAGE_KEY);
          localW = ws ? JSON.parse(ws) : [];
          localC = cs ? JSON.parse(cs) : [];
        } catch {
          /* ignore */
        }

        if (cancelled) return;

        if (remote != null) {
          setWorkflows(remote.savedWorkflows);
          setCanvasNodes(remote.canvasNodes);
        } else if (localW.length > 0 || localC.length > 0) {
          setWorkflows(Array.isArray(localW) ? localW : []);
          setCanvasNodes(Array.isArray(localC) ? localC : []);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('[WorkflowsPage] load workspace failed', e);
          setPersistError('Could not load saved workflows from the server.');
        }
      } finally {
        if (!cancelled) {
          setHydrated(true);
          allowSaveRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(workflows));
      } catch {
        /* ignore */
      }
    }
  }, [workflows, orgId]);

  useEffect(() => {
    if (!orgId) {
      try {
        localStorage.setItem(CANVAS_STORAGE_KEY, JSON.stringify(canvasNodes));
      } catch {
        /* ignore */
      }
    }
  }, [canvasNodes, orgId]);

  useEffect(() => {
    if (!hydrated || !orgId || !allowSaveRef.current) return;

    const t = window.setTimeout(() => {
      setPersisting(true);
      setPersistError(null);
      workflowService
        .saveWorkspace(orgId, { savedWorkflows: workflows, canvasNodes })
        .then(() => {
          try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(CANVAS_STORAGE_KEY);
          } catch {
            /* ignore */
          }
        })
        .catch((e: Error) => {
          console.warn('[WorkflowsPage] save workspace failed', e);
          setPersistError(e.message || 'Save failed');
        })
        .finally(() => setPersisting(false));
    }, 650);

    return () => window.clearTimeout(t);
  }, [workflows, canvasNodes, orgId, hydrated]);

  const refreshExecutions = useCallback(() => {
    if (!orgId) return;
    setExecLoading(true);
    workflowService
      .listRecentExecutions(orgId, 30)
      .then(setExecutions)
      .catch(() => setExecutions([]))
      .finally(() => setExecLoading(false));
  }, [orgId]);

  useEffect(() => {
    if (orgId && hydrated) refreshExecutions();
  }, [orgId, hydrated, refreshExecutions]);

  /** Deep link from Process Orchestrator: `/workflows?focus=new` */
  useEffect(() => {
    if (searchParams.get('focus') !== 'new') return;
    setWorkspaceTab('list');
    setEditingRuleId(null);
    setForm({ name: '', trigger: TRIGGERS[0], action: ACTIONS[0] });
    setShowForm(true);
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const resetForm = () => {
    setShowForm(false);
    setEditingRuleId(null);
    setForm({ name: '', trigger: TRIGGERS[0], action: ACTIONS[0] });
  };

  const startEditRule = (w: WorkflowRule) => {
    setWorkspaceTab('list');
    setEditingRuleId(w.id);
    setForm({ name: w.name, trigger: w.trigger, action: w.action });
    setShowForm(true);
  };

  const saveRule = () => {
    if (!form.name.trim()) return;
    if (editingRuleId) {
      setWorkflows((w) =>
        w.map((x) =>
          x.id === editingRuleId
            ? { ...x, name: form.name.trim(), trigger: form.trigger, action: form.action }
            : x
        )
      );
    } else {
      setWorkflows((w) => [
        ...w,
        {
          id: crypto.randomUUID(),
          name: form.name.trim(),
          trigger: form.trigger,
          action: form.action,
          created: new Date().toISOString(),
        },
      ]);
    }
    resetForm();
  };

  const remove = (id: string) => {
    setWorkflows((w) => w.filter((x) => x.id !== id));
    if (editingRuleId === id) resetForm();
  };

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const x = Math.max(0, e.clientX - rect.left - 80);
      const y = Math.max(0, e.clientY - rect.top - 24);
      if (draggingNode) {
        setCanvasNodes((n) => n.map((node) => (node.id === draggingNode ? { ...node, x, y } : node)));
        setDraggingNode(null);
      } else if (draggedPaletteItem) {
        setCanvasNodes((nodes) => [
          ...nodes,
          {
            id: crypto.randomUUID(),
            type: draggedPaletteItem.type,
            label: draggedPaletteItem.label,
            x,
            y,
          },
        ]);
        setDraggedPaletteItem(null);
      }
    },
    [draggedPaletteItem, draggingNode]
  );

  const handleCanvasDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleNodeDragStart = (e: React.DragEvent, id: string) => {
    setDraggingNode(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleNodeDragEnd = () => setDraggingNode(null);

  const storageHint =
    orgId != null
      ? 'Rules and canvas sync to your organisation in the database (migration 018).'
      : 'Join an organisation to persist workflows to the database; until then, data stays in this browser.';

  const loadStarterPack = () => {
    const starter: WorkflowRule[] = [
      {
        id: crypto.randomUUID(),
        name: 'Shortlist — notify team',
        trigger: 'Status Changed',
        action: 'Send Notification',
        created: new Date().toISOString(),
      },
      {
        id: crypto.randomUUID(),
        name: 'New application — email hook',
        trigger: 'Application Created',
        action: 'Send Email',
        created: new Date().toISOString(),
      },
      {
        id: crypto.randomUUID(),
        name: 'Interview booked — reminder',
        trigger: 'Schedule',
        action: 'Send Notification',
        created: new Date().toISOString(),
      },
    ];
    const names = new Set(workflows.map((w) => w.name));
    const merged = [...workflows, ...starter.filter((s) => !names.has(s.name))];
    if (merged.length === workflows.length) return;
    setWorkflows(merged);
    if (orgId) {
      void orgService.updateOrg(orgId, { onboarding_workflows_completed: true }).catch(() => {
        /* optional column / RLS — ignore */
      });
    }
  };

  return (
    <div>
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Workflows' }]}
        breadcrumbRowEnd={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Link to="/orchestrator" className="btn-secondary inline-flex items-center gap-1.5 text-xs">
              <GitBranch className="h-3.5 w-3.5" /> Process Orchestrator
            </Link>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-1.5 text-xs"
              disabled={!orgId}
              title={orgId ? 'Add sample rules (ORC-070)' : 'Join an organisation first'}
              onClick={() => {
                setWorkspaceTab('list');
                loadStarterPack();
              }}
            >
              <Sparkles className="h-3.5 w-3.5" /> Starter pack
            </button>
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1.5 text-xs"
              onClick={() => {
                setWorkspaceTab('list');
                if (showForm) {
                  resetForm();
                } else {
                  setEditingRuleId(null);
                  setForm({ name: '', trigger: TRIGGERS[0], action: ACTIONS[0] });
                  setShowForm(true);
                }
              }}
            >
              <Plus className="h-3.5 w-3.5" /> {showForm ? 'Cancel' : 'New workflow'}
            </button>
          </div>
        }
      />

      <div className="flex gap-1 mb-4 border-b vscode-border">
        <button
          type="button"
          className={`px-3 py-2 text-xs font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
            workspaceTab === 'list'
              ? 'vscode-text-primary border-brand-500 bg-gray-900/50'
              : 'vscode-text-tertiary border-transparent hover:vscode-text-secondary'
          }`}
          onClick={() => setWorkspaceTab('list')}
        >
          List
        </button>
        <button
          type="button"
          className={`px-3 py-2 text-xs font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
            workspaceTab === 'canvas'
              ? 'vscode-text-primary border-brand-500 bg-gray-900/50'
              : 'vscode-text-tertiary border-transparent hover:vscode-text-secondary'
          }`}
          onClick={() => setWorkspaceTab('canvas')}
        >
          Canvas
        </button>
      </div>

      {workspaceTab === 'list' && orgId && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold vscode-text-primary flex items-center gap-2">
              <Clock className="w-4 h-4 text-brand-400 shrink-0" aria-hidden /> Recent runs
            </h2>
            <button
              type="button"
              onClick={refreshExecutions}
              className="btn-secondary text-xs py-1 px-2 flex items-center gap-1"
              disabled={execLoading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${execLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          <p className="text-xs vscode-text-tertiary mb-3">
            Rows from <code className="vscode-text-secondary">ggj_b2b_workflow_executions</code> (ORC-020). A worker or triggers will insert runs when automations execute.
          </p>
          {executions.length === 0 ? (
            <p className="text-sm vscode-text-tertiary">No runs recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide vscode-text-tertiary border-b vscode-border">
                    <th className="pb-2 pr-4">Started</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2 pr-4">Definition</th>
                    <th className="pb-2">Error / payload</th>
                  </tr>
                </thead>
                <tbody>
                  {executions.map((ex) => (
                    <tr key={ex.id} className="border-b vscode-border">
                      <td className="py-2 pr-4 vscode-text-secondary whitespace-nowrap">
                        {formatDateTimeGmt(ex.started_at)}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-200">{ex.status}</span>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs vscode-text-secondary">
                        {ex.workflow_definition_id ? `${ex.workflow_definition_id.slice(0, 8)}…` : '—'}
                      </td>
                      <td className="py-2 text-xs vscode-text-tertiary max-w-[220px] truncate" title={ex.error ?? ''}>
                        {ex.error ?? JSON.stringify(ex.trigger_payload ?? {}).slice(0, 96)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {persistError && (
        <div className="mb-4 rounded-lg border border-amber-700/50 bg-amber-950/40 px-4 py-2 text-sm text-amber-200">
          {persistError}
        </div>
      )}

      {orgId && (persisting || !hydrated) && !persistError && (
        <p className="mb-4 text-xs vscode-text-tertiary">{!hydrated ? 'Loading saved workspace…' : 'Saving…'}</p>
      )}

      {workspaceTab === 'list' && showForm && (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold vscode-text-primary mb-3">
            {editingRuleId ? 'Edit workflow' : 'Create workflow'}
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs vscode-text-tertiary mb-1">Name</label>
              <input
                className="input w-full"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Auto-notify on shortlist"
              />
            </div>
            <div>
              <label className="block text-xs vscode-text-tertiary mb-1">When (Trigger)</label>
              <select
                className="input w-full"
                value={form.trigger}
                onChange={(e) => setForm((f) => ({ ...f, trigger: e.target.value }))}
              >
                {TRIGGERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs vscode-text-tertiary mb-1">Then (Action)</label>
              <select
                className="input w-full"
                value={form.action}
                onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs vscode-text-tertiary mt-2">{storageHint}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn-primary" onClick={saveRule}>
              {editingRuleId ? 'Save changes' : 'Create'}
            </button>
            <button type="button" className="btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {workspaceTab === 'canvas' && (
      <div className="card overflow-hidden p-0">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-56 border-b lg:border-b-0 lg:border-r vscode-border p-4 shrink-0">
            <p className="text-xs font-bold uppercase tracking-wide vscode-text-tertiary mb-2">Drag to canvas</p>
            <div className="space-y-2">
              <p className="text-xs vscode-text-tertiary">Triggers</p>
              {TRIGGERS.map((t) => (
                <div
                  key={t}
                  draggable
                  onDragStart={() => setDraggedPaletteItem({ type: 'trigger', label: t })}
                  onDragEnd={() => setDraggedPaletteItem(null)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing text-sm text-gray-200 hover:border-brand-500/50"
                  style={{ backgroundColor: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)' }}
                >
                  <Zap className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />
                  {t}
                </div>
              ))}
              <p className="text-xs vscode-text-tertiary mt-3">Actions</p>
              {ACTIONS.map((a) => (
                <div
                  key={a}
                  draggable
                  onDragStart={() => setDraggedPaletteItem({ type: 'action', label: a })}
                  onDragEnd={() => setDraggedPaletteItem(null)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing text-sm text-gray-200 hover:border-green-500/50"
                  style={{ backgroundColor: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)' }}
                >
                  <ArrowRight className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />
                  {a}
                </div>
              ))}
            </div>
          </div>

          <div
            className="flex-1 min-h-[400px] bg-gray-950/50 relative p-4"
            onDragLeave={() => setDraggedPaletteItem(null)}
          >
            <p className="text-xs vscode-text-tertiary mb-2">Canvas — drop nodes here and drag to arrange</p>
            <div
              className="relative w-full h-[360px] rounded-lg border-2 border-dashed border-gray-700 bg-gray-900/50"
              onDrop={handleCanvasDrop}
              onDragOver={handleCanvasDragOver}
            >
              {canvasNodes.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <GitBranch className="w-12 h-12 vscode-text-tertiary" />
                  <span className="ml-2 vscode-text-tertiary">Drop triggers or actions here</span>
                </div>
              )}
              {canvasNodes.map((node) => (
                <div
                  key={node.id}
                  draggable
                  onDragStart={(e) => handleNodeDragStart(e, node.id)}
                  onDragEnd={handleNodeDragEnd}
                  className={`absolute w-40 rounded-lg px-3 py-2 cursor-grab active:cursor-grabbing border shadow-lg ${
                    node.type === 'trigger'
                      ? 'bg-brand-900/80 border-brand-600 text-brand-100'
                      : 'bg-green-900/80 border-green-600 text-green-100'
                  } ${draggingNode === node.id ? 'opacity-80' : ''}`}
                  style={{ left: node.x, top: node.y }}
                >
                  <div className="flex items-center gap-2">
                    {node.type === 'trigger' ? (
                      <Zap className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ArrowRight className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium truncate">{node.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}

      {workspaceTab === 'list' && workflows.length > 0 && (
        <div className="card mt-6 overflow-hidden p-0">
          <h3 className="px-4 py-3 text-sm font-bold vscode-text-primary border-b vscode-border">Saved workflows</h3>
          <table className="ggj-data-table">
            <thead>
              <tr className="border-b vscode-border text-xs font-semibold uppercase tracking-wide vscode-text-tertiary">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Trigger</th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {workflows.map((w) => (
                <tr key={w.id} className="border-b vscode-border last:border-0">
                  <td className="px-4 py-3 font-medium vscode-text-primary">{w.name}</td>
                  <td className="px-4 py-3 vscode-text-secondary">{w.trigger}</td>
                  <td className="px-4 py-3 vscode-text-secondary">{w.action}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title="Edit rule"
                        onClick={() => startEditRule(w)}
                        className="p-1 vscode-text-tertiary hover:text-brand-400"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title="Delete rule"
                        onClick={() => remove(w.id)}
                        className="p-1 vscode-text-tertiary hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!showForm && <p className="mt-4 text-xs vscode-text-tertiary">{storageHint}</p>}
    </div>
  );
}
